import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import portalService from '../../services/portalService';
import PortalShell from './PortalShell';
import PortalCityPicker, { getCachedCityCoords, geocodeCityLabel } from './PortalCityPicker';
import PortalVenuePicker from './PortalVenuePicker';
import { detectMyLocation } from './PortalLocationPicker';
import LinksEditor from '../../components/LinksEditor';

function slugify(text) {
  return (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/**
 * /portal/signals/new (and /:eventId/edit) — Send a Signal.
 *
 * Simplified to a single screen (no wizard) mirroring the regular Portal
 * signal composer, but in Portal Noir styling. Signals are never charged for, so the
 * old capacity / cost / external-ticketing fields are gone. Editing reuses this
 * exact screen (prefilled).
 *   1. The note (what you want to do)
 *   2. Who can join (Open / Curated)
 *   3. Where + when (city, optional venue, date/time)
 *   4. A give-and-take board or not (off by default)
 */
export default function PortalSendSignalPage() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const isEdit = !!eventId;
  const [loadingExisting, setLoadingExisting] = useState(isEdit);
  const [mode, setMode] = useState('open');
  const [allCities, setAllCities] = useState([]);
  const [cities, setCities] = useState([]);
  /** Map of slug → {lat, lng} captured from Mapbox picks this session. */
  const [cityCoords, setCityCoords] = useState({});
  const [venue, setVenue] = useState(null); // { name, address, lat, lng } | null
  const [dateTime, setDateTime] = useState(null); // Date | null
  const [signalText, setSignalText] = useState('');
  const [links, setLinks] = useState([]); // [{url, text}]
  const [giveTake, setGiveTake] = useState(false); // give-and-take board, off by default
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [myLocation, setMyLocation] = useState(null); // {lat,lng,label} — orders the city quick-pick by proximity

  useEffect(() => { portalService.listCities().then((r) => { if (r.success) setAllCities(r.data || []); }); }, []);

  // The member's saved Portal Noir location anchors the city quick-pick ordering.
  useEffect(() => {
    portalService.getMyLocation().then((r) => {
      if (r.success && r.data?.latitude != null) {
        setMyLocation({ lat: r.data.latitude, lng: r.data.longitude, label: r.data.label });
      }
    });
  }, []);

  // Edit mode: pull the existing Signal and prefill.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    portalService.getSignal(eventId).then((r) => {
      if (cancelled) return;
      if (!r.success || !r.data) { navigate('/noir/home', { replace: true }); return; }
      const s = r.data;
      if (!s.isMine) { navigate(`/noir/signals/${eventId}`, { replace: true }); return; }
      setMode(s.mode === 'curated' ? 'curated' : 'open');
      setCities(s.cities || []);
      setSignalText(s.signalText || '');
      setLinks(Array.isArray(s.links) ? s.links : []);
      setGiveTake(!!s.giveTakeEnabled);
      setDateTime(s.dateTimeUtc ? new Date(s.dateTimeUtc) : null);
      if (s.venueName) setVenue({ name: s.venueName, address: s.venueAddress || '' });
      const primary = (s.cities || [])[0];
      if (primary && typeof s.latitude === 'number' && typeof s.longitude === 'number') {
        setCityCoords({ [primary]: { lat: s.latitude, lng: s.longitude } });
        try {
          const cache = JSON.parse(localStorage.getItem('portal:cityLabels') || '{}');
          if (s.primaryCityLabel) { cache[primary] = s.primaryCityLabel; localStorage.setItem('portal:cityLabels', JSON.stringify(cache)); }
          const coords = JSON.parse(localStorage.getItem('portal:cityCoords') || '{}');
          coords[primary] = { lat: s.latitude, lng: s.longitude };
          localStorage.setItem('portal:cityCoords', JSON.stringify(coords));
        } catch {}
      }
      setLoadingExisting(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Auto-detect current location once and pre-select it as the primary city.
  useEffect(() => {
    let cancelled = false;
    if (isEdit) return; // editing: keep the saved city
    if (cities.length > 0) return;
    setAutoDetecting(true);
    (async () => {
      const result = await detectMyLocation();
      if (cancelled || !result || cities.length > 0) { setAutoDetecting(false); return; }
      const { lat, lng, label } = result;
      const head = (label || '').split(',')[0]?.trim();
      const slug = slugify(head || label);
      if (!slug) { setAutoDetecting(false); return; }
      setCityCoords((m) => ({ ...m, [slug]: { lat, lng } }));
      try {
        const cache = JSON.parse(localStorage.getItem('portal:cityLabels') || '{}');
        cache[slug] = label || head;
        localStorage.setItem('portal:cityLabels', JSON.stringify(cache));
      } catch {}
      setCities([slug]);
      setAutoDetecting(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Primary city → coords lookup for the venue picker's proximity bias.
  const primarySlug = cities[0];
  const primaryCoords = primarySlug ? (cityCoords[primarySlug] || getCachedCityCoords(primarySlug)) : null;
  const primaryLabel = primarySlug
    ? (allCities.find((c) => c.slug === primarySlug)?.label
       || (() => { try { return JSON.parse(localStorage.getItem('portal:cityLabels') || '{}')[primarySlug] || ''; } catch { return ''; } })()
       || primarySlug)
    : '';

  // When the user removes the primary city, drop the venue anchored to it.
  useEffect(() => {
    if (!primarySlug && venue) setVenue(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primarySlug]);

  const submit = async () => {
    setBusy(true); setError(null);
    if (signalText.trim().length < 4) { setBusy(false); setError('Say what you want to do.'); return; }
    if (cities.length === 0) { setBusy(false); setError('Pick at least one city.'); return; }
    if (!dateTime) { setBusy(false); setError('Pick a date and time.'); return; }

    let coords;
    if (venue && typeof venue.lat === 'number' && typeof venue.lng === 'number') {
      coords = { lat: venue.lat, lng: venue.lng };
    } else {
      coords = primaryCoords;
      if (!coords) {
        const label = allCities.find((c) => c.slug === primarySlug)?.label || primarySlug;
        coords = await geocodeCityLabel(label);
      }
    }
    if (!coords) { setBusy(false); setError('Could not geocode that city. Pick another or search again.'); return; }

    const payload = {
      mode,
      cities,
      latitude: coords.lat,
      longitude: coords.lng,
      dateTimeUtc: dateTime.toISOString(),
      signalText,
      // Signals are never charged for — no capacity/cost/external ticketing.
      capacity: null,
      costCents: 0,
      isExternalTicketing: false,
      externalTicketUrl: null,
      externalTicketDisplayPrice: null,
      giveTakeEnabled: giveTake,
      links: links.length > 0 ? links : null,
      venueName: venue?.name || null,
      venueAddress: venue?.address || null,
    };
    const r = isEdit
      ? await portalService.updateSignal(eventId, payload)
      : await portalService.createSignal(payload);
    setBusy(false);
    if (r.success) navigate(isEdit ? `/noir/signals/${eventId}` : '/noir/home', { replace: true });
    else setError(r.error || (isEdit ? 'Could not save changes' : 'Could not send signal'));
  };

  if (loadingExisting) {
    return (
      <PortalShell viewerState="accepted">
        <div style={{ padding: 80, textAlign: 'center' }} className="portal-mono">LOADING…</div>
      </PortalShell>
    );
  }

  return (
    <PortalShell viewerState="accepted">
      <div className="wrap" style={{ maxWidth: 720, paddingTop: 'clamp(36px,6vw,64px)', paddingBottom: 100 }}>
        <div className="formhead">
          <button className="backlink" style={{ display: 'block', marginBottom: 22 }} onClick={() => navigate(isEdit ? `/noir/signals/${eventId}` : '/noir/home')}>← BACK</button>
          <span className="kicker kicker--gold" style={{ display: 'block' }}>{isEdit ? 'EDIT SIGNAL' : 'SEND A SIGNAL'}</span>
          <h1 className="display" style={{ fontSize: 'clamp(40px,6vw,72px)', marginTop: 24 }}>
            {isEdit ? <>Tune the <em>note</em>.</> : <>Find your <em>people</em>.</>}
          </h1>
          <p className="lede" style={{ marginTop: 20, maxWidth: '38ch' }}>Say what you want to do, where, and when. The rest is gravy.</p>
        </div>

        <div className="enter" style={{ marginTop: 34 }}>
          <div className="fstack">
            {/* 1. The note */}
            <div className="field">
              <span className="field__label">What do you want to do?</span>
              <textarea
                className="textarea"
                maxLength={500}
                placeholder="In London for the day. Heading to the Black Sheep Cafe in Mayfair at 2 PM. Who wants to join me for a co-working sesh?"
                value={signalText}
                onChange={(e) => setSignalText(e.target.value.slice(0, 500))}
              />
              <p className="charcount">{signalText.length} / 500</p>
            </div>

            {/* 2. Who can join */}
            <div className="field">
              <span className="field__label">Who can join</span>
              <div className="choice">
                <button type="button" className={'opt' + (mode === 'open' ? ' opt--on' : '')} onClick={() => setMode('open')}>
                  <span className="opt__tick">✓</span>
                  <div className="opt__h">Open<span className="dot">.</span></div>
                  <p className="opt__d">Everyone&rsquo;s welcome.</p>
                </button>
                <button type="button" className={'opt' + (mode === 'curated' ? ' opt--on' : '')} onClick={() => setMode('curated')}>
                  <span className="opt__tick">✓</span>
                  <div className="opt__h">Curated<span className="dot">.</span></div>
                  <p className="opt__d">You choose who&rsquo;s in.</p>
                </button>
              </div>
            </div>

            {/* 3. Where + when */}
            <div className="field">
              <span className="field__label">City {autoDetecting && '· detecting your location…'}</span>
              <PortalCityPicker
                single
                nearLocation={myLocation}
                selected={cities}
                onChange={(next) => { if (next[0] !== cities[0]) setVenue(null); setCities(next); }}
                allCities={allCities}
                onPickCoords={({ slug, lat, lng }) => setCityCoords((m) => ({ ...m, [slug]: { lat, lng } }))}
              />
            </div>
            <div className="frow">
              <div className="field">
                <span className="field__label">Venue · optional</span>
                <PortalVenuePicker value={venue} onChange={setVenue} cityCoords={primaryCoords} cityLabel={primaryLabel} />
              </div>
              <div className="field">
                <span className="field__label">When</span>
                <div className="portal-datepicker-wrap">
                  <DatePicker
                    selected={dateTime}
                    onChange={(d) => setDateTime(d)}
                    showTimeSelect timeIntervals={15} timeFormat="h:mm aa"
                    dateFormat="EEE, MMM d, yyyy · h:mm aa"
                    minDate={new Date()} placeholderText="e.g. Sat · 8:00 PM"
                    className="input" popperPlacement="bottom-start" showPopperArrow={false}
                  />
                </div>
              </div>
            </div>

            {/* Optional links */}
            <div className="field">
              <span className="field__label">Add a link · optional</span>
              <LinksEditor value={links} onChange={setLinks} theme="portal" />
            </div>

            {/* 4. Give & take board */}
            <div className="field">
              <span className="field__label">Give &amp; take board</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={giveTake} onChange={(e) => setGiveTake(e.target.checked)} />
                <span>Let people offer to bring things and claim what&rsquo;s needed.</span>
              </label>
            </div>
          </div>

          {error && <p className="meta" style={{ color: '#B4736A', marginBottom: 12 }}>{error}</p>}
          <button onClick={submit} disabled={busy} className="btn btn--gold">
            {busy ? 'SAVING…' : <>{isEdit ? 'SAVE CHANGES' : 'SEND IT'} <span className="arr">→</span></>}
          </button>
        </div>
      </div>
    </PortalShell>
  );
}
